
import React, { useState, useEffect, useRef } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isToday, addMonths, subMonths, addYears, subYears, startOfWeek, endOfWeek, isSameMonth, setYear, setMonth, getYear, getMonth } from 'date-fns';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Calendar, AlertCircle, ChevronDown } from 'lucide-react';

const CustomDatePicker = ({ 
  value, 
  onChange, 
  allOrders = {}, 
  branches = [], 
  showCapacityInfo = true,
  placeholder = "dd-mm-yyyy",
  containerClassName = "",
  inputClassName = "",
  calendarClassName = "",
  buttonClassName = "",
  iconClassName = "",
  error = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [hoveredDate, setHoveredDate] = useState(null);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState({ 
    top: 'auto', 
    bottom: 'auto', 
    left: 'auto', 
    right: 'auto',
    transform: 'none'
  });
  const [capacityCardPosition, setCapacityCardPosition] = useState({ show: false, side: 'right', top: 0 });
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningDate, setWarningDate] = useState(null);
  
  const inputRef = useRef(null);
  const calendarRef = useRef(null);
  const yearDropdownRef = useRef(null);
  const monthDropdownRef = useRef(null);

  // Generate years from 1900 to current year + 10
  const currentYear = new Date().getFullYear() + 10;
  const years = Array.from({ length: currentYear - 1900 + 1 }, (_, i) => currentYear - i);
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // --- STYLING CONSTANTS (Matched to SelectDropdown) ---
  
  const defaultContainerClass = "relative w-full";
  
  // Matches SelectDropdown input style
  // const defaultInputClass = `
  //   w-full flex justify-between items-center border rounded-[7px] py-1.5 px-3 text-left shadow-sm focus:outline-none transition-all duration-200
  //   bg-white dark:bg-surface
  //   ${error
  //     ? 'border-highlight dark:border-highlight' // Assuming 'highlight' is used for errors based on SelectDropdown
  //     : (value
  //       ? "border-primary dark:border-primary text-primary" 
  //       : "border-neutral/20 dark:border-neutral/30 text-textPrimary")
  //   }
  //   hover:border-primary dark:hover:border-primary
  // `;

  const defaultInputClass = `
  w-full flex justify-between items-center
  bg-gray-100 border border-gray-200
  rounded-md px-4 py-2.5 text-sm text-gray-800
  focus:outline-none focus:ring-0
`;


  // Matches SelectDropdown dropdown style
  const defaultCalendarClass = `
    absolute z-[999] mt-1 rounded-[7px] border shadow-lg 
    bg-white dark:bg-surface border-neutral/20 dark:border-neutral/30
    text-textPrimary
  `;

  const defaultButtonClass = "relative h-7 text-xs rounded transition-all";
  const defaultIconClass = "w-4 h-4";

  // --- LOGIC ---

  const getBranchCapacity = (branchCode) => {
    const branch = branches.find(b => b.branchCode === branchCode);
    return branch?.branchCapacity; 
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (yearDropdownRef.current && !yearDropdownRef.current.contains(event.target)) {
        setShowYearDropdown(false);
      }
      if (monthDropdownRef.current && !monthDropdownRef.current.contains(event.target)) {
        setShowMonthDropdown(false);
      }
    };
    if (showYearDropdown || showMonthDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showYearDropdown, showMonthDropdown]);

  const handleYearSelect = (year) => {
    const newDate = setYear(currentMonth, year);
    setCurrentMonth(newDate);
    setShowYearDropdown(false);
  };

  const handleMonthSelect = (monthIndex) => {
    const newDate = setMonth(currentMonth, monthIndex);
    setCurrentMonth(newDate);
    setShowMonthDropdown(false);
  };

  useEffect(() => {
    if (isOpen && inputRef.current && calendarRef.current) {
      const inputRect = inputRef.current.getBoundingClientRect();
      const calendarWidth = 260;
      const calendarHeight = 320;
      const padding = 10;
      const viewport = { width: window.innerWidth, height: window.innerHeight };
      
      let position = { top: 'auto', bottom: 'auto', left: 'auto', right: 'auto', transform: 'none' };

      const spaceBelow = viewport.height - inputRect.bottom - padding;
      const spaceAbove = inputRect.top - padding;
      
      if (spaceBelow >= calendarHeight) {
        position.top = '100%';
      } else if (spaceAbove >= calendarHeight) {
        position.bottom = '100%';
      } else {
        position.top = '100%';
      }

      const spaceOnRight = viewport.width - inputRect.left - padding;
      const spaceOnLeft = inputRect.right - padding;
      
      if (spaceOnRight >= calendarWidth) {
        position.left = '0';
      } else if (spaceOnLeft >= calendarWidth) {
        position.right = '0';
      } else {
        const leftOffset = (viewport.width - calendarWidth) / 2 - inputRect.left;
        position.left = `${leftOffset}px`;
      }
      setCalendarPosition(position);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setHoveredDate(null);
      setCapacityCardPosition({ show: false, side: 'right', top: 0 });
      setShowYearDropdown(false);
      setShowMonthDropdown(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target) && 
          inputRef.current && !inputRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const getCapacityInfo = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const orders = allOrders[dateStr] || [];
    
    const ordersByBranch = orders.reduce((acc, order) => {
      if (!acc[order.branchCode]) acc[order.branchCode] = [];
      acc[order.branchCode].push(order);
      return acc;
    }, {});

    const branchCapacities = Object.entries(ordersByBranch).map(([branchCode, branchOrders]) => {
      const capacity = getBranchCapacity(branchCode);
      const used = branchOrders.length;
      const percentage = (used / capacity) * 100;
      return { branchCode, capacity, used, remaining: capacity - used, percentage };
    });

    const activeBranches = Object.keys(ordersByBranch);
    const totalCapacity = activeBranches.length > 0 
      ? activeBranches.reduce((sum, branchCode) => sum + getBranchCapacity(branchCode), 0)
      : branches.reduce((sum, branch) => sum + branch.branchCapacity, 0);
    
    const totalUsed = orders.length;
    const totalRemaining = totalCapacity - totalUsed;
    const overallPercentage = totalCapacity > 0 ? (totalUsed / totalCapacity) * 100 : 0;
    const maxBranchPercentage = branchCapacities.length > 0 ? Math.max(...branchCapacities.map(b => b.percentage)) : 0;

    return { used: totalUsed, remaining: totalRemaining, percentage: overallPercentage, maxBranchPercentage, orders, dateStr, branchCapacities, totalCapacity };
  };

  const getCapacityColor = (capacityInfo) => {
    const percentage = capacityInfo.maxBranchPercentage || capacityInfo.percentage;
    if (percentage === 0) return 'text-textPrimary';
    // Adaptive colors for light/dark mode
    if (percentage <= 50) return 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400';
    if (percentage < 90) return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20 dark:text-yellow-400';
    return 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400';
  };

  const handleDateHover = (date, event) => {
    if (!showCapacityInfo) return;
    const capacityInfo = getCapacityInfo(date);
    if (capacityInfo.used > 0) {
      setHoveredDate(date);
      const buttonRect = event.target.getBoundingClientRect();
      const calendarRect = calendarRef.current?.getBoundingClientRect();
      
      if (calendarRect) {
        const isMobile = window.innerWidth < 640;
        const cardWidth = 200;
        const cardHeight = 180;
        let side = 'right';
        let topPosition = buttonRect.top - calendarRect.top;
        const spaceOnRight = window.innerWidth - calendarRect.right;
        const spaceOnLeft = calendarRect.left;
        
        if (isMobile) {
          side = 'inside';
          topPosition = 'auto';
        } else if (spaceOnRight < cardWidth && spaceOnLeft > cardWidth) {
          side = 'left';
        }
        
        if (topPosition + cardHeight > calendarRect.height) {
          topPosition = Math.max(0, topPosition - cardHeight + 30);
        }
        setCapacityCardPosition({ show: true, side, top: topPosition });
      }
    }
  };

  const handleDateSelect = (date) => {
    setHoveredDate(null);
    setCapacityCardPosition({ show: false, side: 'right', top: 0 });
    
    if (showCapacityInfo) {
      const capacityInfo = getCapacityInfo(date);
      const hasHighCapacityBranch = capacityInfo.branchCapacities.some(branch => branch.percentage >= 90);
      if (hasHighCapacityBranch) {
        setWarningDate(date);
        setShowWarningModal(true);
        return;
      }
    }
    onChange(format(date, 'yyyy-MM-dd'));
    setIsOpen(false);
  };

  const confirmHighCapacityDate = () => {
    if (warningDate) {
      onChange(format(warningDate, 'yyyy-MM-dd'));
      setIsOpen(false);
      setShowWarningModal(false);
      setWarningDate(null);
    }
  };

  const handleClearDate = () => {
    onChange('');
    setIsOpen(false);
  };

  const getAllDaysInView = () => {
    const start = startOfWeek(startOfMonth(currentMonth));
    const end = endOfWeek(endOfMonth(currentMonth));
    return eachDayOfInterval({ start, end });
  };

  const goToPreviousYear = () => setCurrentMonth(subYears(currentMonth, 1));
  const goToNextYear = () => setCurrentMonth(addYears(currentMonth, 1));
  const goToPreviousMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const goToNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  const handleInputClick = (e) => {
    e.preventDefault();
    if(!isOpen) setIsOpen(true);
    else setIsOpen(false);
  };

  useEffect(() => {
    if (showYearDropdown && yearDropdownRef.current) {
      const selectedYear = yearDropdownRef.current.querySelector('[data-selected="true"]');
      if (selectedYear) selectedYear.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [showYearDropdown]);

  return (
    <>
      <div className={`${defaultContainerClass} ${containerClassName}`} ref={inputRef}>
        {/* Date Input */}
        <button
          type="button"
          onClick={handleInputClick}
          className={`${defaultInputClass} ${inputClassName}`}
        >
          <span className="whitespace-nowrap overflow-hidden text-ellipsis">
            {value ? format(new Date(value), 'dd-MM-yyyy') : <span className="text-textMuted">{placeholder}</span>}
          </span>
          <div className="flex items-center gap-1 ml-2">
             <Calendar className={`${defaultIconClass} text-textMuted`} />
          </div>
        </button>

        {/* Calendar Dropdown */}
        {isOpen && (
          <div 
            ref={calendarRef}
            className={`${defaultCalendarClass} ${calendarClassName}`}
            style={{
              ...calendarPosition,
              width: '260px',
              maxWidth: 'calc(100vw - 20px)',
              animation: 'dropdownFade 0.2s ease-out'
            }}
          >
            {/* Navigation */}
            <div className="p-2 border-b border-neutral/10 dark:border-neutral/20 bg-neutralLight/50 dark:bg-neutral/5">
              <div className="flex items-center justify-between text-textPrimary">
                <button type="button" onClick={goToPreviousYear} className="p-1 hover:bg-neutralLight dark:hover:bg-neutral/10 rounded transition-colors text-textMuted hover:text-textPrimary" title="Previous year">
                  <ChevronsLeft className="w-4 h-4" />
                </button>
                <button type="button" onClick={goToPreviousMonth} className="p-1 hover:bg-neutralLight dark:hover:bg-neutral/10 rounded transition-colors text-textMuted hover:text-textPrimary" title="Previous month">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                
                <div className="flex items-center gap-1">
                  {/* Month Dropdown Trigger */}
                  <div className="relative" ref={monthDropdownRef}>
                    <button
                      type="button"
                      onClick={() => { setShowMonthDropdown(!showMonthDropdown); setShowYearDropdown(false); }}
                      className="flex items-center gap-1 px-2 py-1 text-sm font-semibold hover:bg-neutralLight dark:hover:bg-neutral/10 rounded transition-colors"
                    >
                      {format(currentMonth, 'MMM')}
                      <ChevronDown className="w-3 h-3 text-textMuted" />
                    </button>
                    {/* Month Dropdown List */}
                    {showMonthDropdown && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 bg-white dark:bg-surface border border-neutral/20 dark:border-neutral/30 rounded-[7px] shadow-lg z-[70] w-32 max-h-48 overflow-y-auto">
                        {monthNames.map((month, index) => (
                          <button
                            key={month}
                            type="button"
                            onClick={() => handleMonthSelect(index)}
                            className={`block w-full text-left px-3 py-1.5 text-xs transition-colors
                              ${getMonth(currentMonth) === index 
                                ? 'bg-primaryLight dark:bg-primary/10 text-primary font-medium' 
                                : 'text-textPrimary hover:bg-neutralLight dark:hover:bg-neutral/10'}
                            `}
                          >
                            {month}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Year Dropdown Trigger */}
                  <div className="relative" ref={yearDropdownRef}>
                    <button
                      type="button"
                      onClick={() => { setShowYearDropdown(!showYearDropdown); setShowMonthDropdown(false); }}
                      className="flex items-center gap-1 px-2 py-1 text-sm font-semibold hover:bg-neutralLight dark:hover:bg-neutral/10 rounded transition-colors"
                    >
                      {format(currentMonth, 'yyyy')}
                      <ChevronDown className="w-3 h-3 text-textMuted" />
                    </button>
                    {/* Year Dropdown List */}
                    {showYearDropdown && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 bg-white dark:bg-surface border border-neutral/20 dark:border-neutral/30 rounded-[7px] shadow-lg z-[70] w-20 max-h-48 overflow-y-auto">
                        {years.map((year) => (
                          <button
                            key={year}
                            type="button"
                            data-selected={getYear(currentMonth) === year}
                            onClick={() => handleYearSelect(year)}
                            className={`block w-full text-center px-3 py-1.5 text-xs transition-colors
                              ${getYear(currentMonth) === year 
                                ? 'bg-primaryLight dark:bg-primary/10 text-primary font-medium' 
                                : 'text-textPrimary hover:bg-neutralLight dark:hover:bg-neutral/10'}
                            `}
                          >
                            {year}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                <button type="button" onClick={goToNextMonth} className="p-1 hover:bg-neutralLight dark:hover:bg-neutral/10 rounded transition-colors text-textMuted hover:text-textPrimary" title="Next month">
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button type="button" onClick={goToNextYear} className="p-1 hover:bg-neutralLight dark:hover:bg-neutral/10 rounded transition-colors text-textMuted hover:text-textPrimary" title="Next year">
                  <ChevronsRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="p-2">
              <div className="grid grid-cols-7 gap-0.5 mb-1">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                  <div key={day} className="text-center text-[10px] uppercase tracking-wider text-textMuted font-semibold py-1">
                    {day}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-0.5">
                {getAllDaysInView().map((date, index) => {
                  const isCurrentMonth = isSameMonth(date, currentMonth);
                  const capacityInfo = getCapacityInfo(date);
                  const isSelected = value && format(date, 'yyyy-MM-dd') === value;
                  const colorClass = getCapacityColor(capacityInfo);
                  
                  // Conditional styling
                  let dayClass = `${defaultButtonClass} ${buttonClassName} `;
                  
                  if (isSelected) {
                    dayClass += `bg-primary text-white hover:bg-primary/90 shadow-sm`;
                  } else {
                    if (!isCurrentMonth) {
                       dayClass += `text-textMuted opacity-40`;
                    } else {
                       dayClass += `text-textPrimary hover:bg-neutralLight dark:hover:bg-neutral/10 `;
                       // Apply capacity color background if not selected and has data
                       if (capacityInfo.used > 0 && showCapacityInfo) {
                           dayClass += `${colorClass} `;
                       }
                    }
                    if (isToday(date)) {
                        dayClass += ` ring-1 ring-primary text-primary font-semibold`;
                    }
                  }

                  return (
                    <button
                      type='button'                    
                      key={index}
                      onClick={() => handleDateSelect(date)}
                      onMouseEnter={(e) => handleDateHover(date, e)}
                      onMouseLeave={() => { setHoveredDate(null); setCapacityCardPosition(prev => ({ ...prev, show: false })); }}
                      className={dayClass}
                    >
                      <span>{format(date, 'd')}</span>
                      {showCapacityInfo && capacityInfo.used > 0 && !isSelected && (
                        <div className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full
                          ${capacityInfo.maxBranchPercentage <= 50 ? 'bg-blue-500 dark:bg-blue-400' : ''}
                          ${capacityInfo.maxBranchPercentage > 50 && capacityInfo.maxBranchPercentage < 90 ? 'bg-yellow-500 dark:bg-yellow-400' : ''}
                          ${capacityInfo.maxBranchPercentage >= 90 ? 'bg-red-500 dark:bg-red-400' : ''}
                        `} />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Footer Buttons */}
              <div className="mt-2 pt-2 border-t border-neutral/10 dark:border-neutral/20 flex justify-between">
                <button
                  type='button'
                  onClick={handleClearDate}
                  className="text-xs text-textMuted hover:text-textPrimary font-medium py-1 px-2 hover:bg-neutralLight dark:hover:bg-neutral/10 rounded transition-colors"
                >
                  Clear
                </button>
                <button
                  type='button'
                  onClick={() => { const today = new Date(); setCurrentMonth(today); handleDateSelect(today); }}
                  className="text-xs text-primary hover:text-primary/80 font-medium py-1 px-2 hover:bg-primaryLight dark:hover:bg-primary/10 rounded transition-colors"
                >
                  Today
                </button>
              </div>
            </div>

            {/* Capacity Tooltip */}
            {showCapacityInfo && hoveredDate && capacityCardPosition.show && (
              <div 
                className={`
                  ${capacityCardPosition.side === 'inside' ? 'absolute bottom-0 left-0 right-0 border-t' : `absolute ${capacityCardPosition.side === 'right' ? 'left-full ml-1' : 'right-full mr-1'}`}
                  bg-white dark:bg-surface border border-neutral/20 dark:border-neutral/30 rounded-[7px] shadow-xl p-3 z-[1000] min-w-[200px]
                `}
                style={capacityCardPosition.side !== 'inside' ? { top: `${capacityCardPosition.top}px` } : {}}
              >
                <div className="text-xs text-textPrimary">
                  <div className="font-semibold mb-2 pb-1 border-b border-neutral/10 dark:border-neutral/20">
                    {format(hoveredDate, 'dd MMM yyyy')}
                  </div>
                  
                  <div className="flex justify-between mb-2 text-textMuted">
                    <span>Total Orders:</span>
                    <span className="font-medium text-textPrimary">{getCapacityInfo(hoveredDate).used}</span>
                  </div>

                  {getCapacityInfo(hoveredDate).branchCapacities.length > 0 && (
                    <div className="space-y-1.5">
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-textMuted">By Branch</div>
                      {getCapacityInfo(hoveredDate).branchCapacities.map((branch) => (
                        <div key={branch.branchCode} className="flex justify-between items-center">
                          <span className="text-textPrimary">{branch.branchCode}:</span>
                          <div className="flex items-center gap-2">
                            <span className="text-textMuted">{branch.used}/{branch.capacity}</span>
                            <span className={`px-1.5 py-0.5 rounded-[4px] text-[10px] font-medium
                              ${branch.percentage <= 50 ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' : ''}
                              ${branch.percentage > 50 && branch.percentage < 90 ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300' : ''}
                              ${branch.percentage >= 90 ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' : ''}
                            `}>
                              {branch.percentage.toFixed(0)}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {getCapacityInfo(hoveredDate).branchCapacities.some(b => b.percentage >= 90) && (
                    <div className="mt-2 pt-2 border-t border-neutral/10 dark:border-neutral/20 text-red-600 dark:text-red-400 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      <span>High capacity warning</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Warning Modal */}
      {showWarningModal && warningDate && (
        <div className="fixed inset-0 z-[1100] flex items-start justify-center pt-20">
          <div className="fixed inset-0 bg-neutral/60 dark:bg-black/60 backdrop-blur-sm" onClick={() => setShowWarningModal(false)} />
          
          <div className="relative bg-white dark:bg-surface border border-neutral/20 dark:border-neutral/30 rounded-lg shadow-2xl p-6 max-w-sm w-full mx-4 animate-slide-down">
            <div className="flex items-start mb-4">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-full mr-3">
                 <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-textPrimary">High Capacity</h3>
                <p className="mt-2 text-sm text-textMuted">
                  The following branches are nearly full on <span className="text-textPrimary font-medium">{format(warningDate, 'dd MMM yyyy')}</span>:
                </p>
                <div className="mt-3 space-y-1 bg-neutralLight dark:bg-neutral/5 p-2 rounded-md">
                  {getCapacityInfo(warningDate).branchCapacities
                    .filter(b => b.percentage >= 90)
                    .map(branch => (
                      <div key={branch.branchCode} className="text-sm flex justify-between">
                        <span className="font-medium text-textPrimary">{branch.branchCode}</span>
                        <span className="text-orange-600 dark:text-orange-400 font-medium">
                          {branch.percentage.toFixed(0)}% ({branch.used}/{branch.capacity})
                        </span>
                      </div>
                    ))}
                </div>
                <p className="mt-3 text-sm text-textMuted">Do you want to proceed?</p>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                type='button'
                onClick={() => setShowWarningModal(false)}
                className="px-4 py-2 text-sm font-medium text-textPrimary bg-neutralLight dark:bg-neutral/10 hover:bg-neutral/20 dark:hover:bg-neutral/20 rounded-[7px] transition-colors"
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={confirmHighCapacityDate}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-[7px] transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes dropdownFade {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-down {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-down { animation: slide-down 0.3s ease-out; }
      `}</style>
    </>
  );
};

export default CustomDatePicker;